import { useCallback, useEffect, useMemo, useState } from "react";
import {
  shellApi,
  type ChatTreatmentStatus,
  type TreatmentQueueItemDto,
} from "../../api/shellApi";
import {
  TREATMENT_STATUS_LABELS,
  TREATMENT_STATUS_STYLES,
  isOpenTreatmentStatus,
} from "./chatTreatmentConstants";

type FilterKey = "all" | ChatTreatmentStatus;

type Props = {
  onOpenMessage: (conversationId: string, messageId: string) => void;
  refreshKey?: number;
};

export function TreatmentQueueList({ onOpenMessage, refreshKey = 0 }: Props) {
  const [filter, setFilter] = useState<FilterKey>("all");
  const [items, setItems] = useState<TreatmentQueueItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await shellApi.listTreatmentQueue(filter === "all" ? null : filter);
      setItems(list);
    } catch {
      setError("שגיאה בטעינת תור הטיפול");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const openCount = useMemo(
    () => items.filter((i) => isOpenTreatmentStatus(i.treatmentStatus)).length,
    [items],
  );

  return (
    <div className="chat-treatment-queue">
      <div className="chat-treatment-queue__filters" role="tablist">
        {(
          [
            ["all", "הכל"],
            ["flagged", "סומן לטיפול"],
            ["in_progress", "בטיפול"],
            ["handled", "טופל"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={filter === key}
            className={`chat-window__tab${filter === key ? " is-active" : ""}`}
            onClick={() => setFilter(key)}
          >
            {label}
            {key === "all" && openCount > 0 ? ` (${openCount})` : ""}
          </button>
        ))}
      </div>
      {loading ? <div className="chat-window__empty">טוען…</div> : null}
      {error ? <div className="chat-window__empty">{error}</div> : null}
      {!loading && !error && items.length === 0 ? (
        <div className="chat-window__empty">
          אין הודעות בתור. סמן הודעה כ«סומן לטיפול» מתוך השיחה.
        </div>
      ) : null}
      <ul className="chat-window__list">
        {items.map((item) => {
          const style = TREATMENT_STATUS_STYLES[item.treatmentStatus];
          return (
            <li key={item.messageId}>
              <button
                type="button"
                className="chat-window__conv"
                onClick={() => onOpenMessage(item.conversationId, item.messageId)}
              >
                <div className="chat-window__conv-main">
                  <strong>{item.conversationName}</strong>
                  <span className="chat-window__conv-preview">{item.contentPreview}</span>
                  <span className="chat-treatment-queue__meta">
                    {item.senderName}
                    <span
                      className="chat-bubble__treatment"
                      style={{
                        background: style.bg,
                        color: style.color,
                        borderColor: style.border,
                      }}
                    >
                      {TREATMENT_STATUS_LABELS[item.treatmentStatus]}
                    </span>
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
